import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Globe, UserRound, ShieldCheck, MessageCircle } from "lucide-react";
import API, { formatPrice, CATEGORY_LABELS } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Reveal";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    API.get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[#faf7f2] pt-[72px]">
        <p className="font-serif-d text-4xl text-[#4b3a2f]">Servicio no encontrado</p>
        <Link to="/catalogo" className="rounded-full bg-[#4b3a2f] px-8 py-3 text-sm text-[#f2e9dd]">
          Volver al catálogo
        </Link>
      </div>
    );

  if (!product)
    return <div className="flex min-h-screen items-center justify-center bg-[#faf7f2] pt-[72px]"><p className="animate-pulse text-[#6b5b52]">Cargando…</p></div>;

  return (
    <div className="min-h-screen bg-[#faf7f2] pb-24 pt-[92px]" data-testid="product-detail-page">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate(-1)}
          data-testid="product-back-button"
          className="mt-6 inline-flex items-center gap-2 text-sm text-[#6b5b52] transition-colors hover:text-[#c9a24b]"
        >
          <ArrowLeft size={16} /> Volver
        </button>

        <div className="mt-8 grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 rounded-t-[999px] rounded-b-3xl border border-[#c9a24b]/40" />
            <div className="arch-frame overflow-hidden">
              <img src={product.image_url} alt={product.title} className="aspect-[4/5] w-full object-cover" />
            </div>
          </motion.div>

          <Reveal className="flex flex-col justify-center" y={20}>
            <span className="inline-flex w-fit items-center rounded-full bg-[#f2e9dd] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#a47b52]">
              {CATEGORY_LABELS[product.category] || product.category}
            </span>
            <h1 className="mt-6 font-serif-d text-4xl font-semibold leading-tight text-[#4b3a2f] sm:text-5xl">
              {product.title}
            </h1>
            <p className="mt-4 font-serif-d text-5xl font-semibold text-[#c9a24b]">{formatPrice(product.price)} <span className="text-lg text-[#6b5b52]">USD</span></p>

            <div className="mt-6 flex flex-wrap gap-5 text-sm text-[#6b5b52]">
              {product.duration && (
                <span className="inline-flex items-center gap-2"><Clock size={16} className="text-[#c9a24b]" /> {product.duration}</span>
              )}
              {product.modality && (
                <span className="inline-flex items-center gap-2"><Globe size={16} className="text-[#c9a24b]" /> {product.modality}</span>
              )}
              {product.instructor && (
                <span className="inline-flex items-center gap-2"><UserRound size={16} className="text-[#c9a24b]" /> {product.instructor}</span>
              )}
            </div>

            <p className="mt-8 text-base leading-relaxed text-[#4b3a2f]/90">{product.description}</p>

            <div className="mt-10 flex flex-wrap gap-4">
              <button
                onClick={() => add(product)}
                data-testid="product-add-to-cart-button"
                className="flex-1 rounded-full bg-[#4b3a2f] px-8 py-4 text-sm font-semibold text-[#f2e9dd] transition-all duration-300 hover:bg-[#c9a24b] hover:text-[#4b3a2f] sm:flex-none"
              >
                Agregar al carrito
              </button>
              <button
                onClick={() => { add(product); navigate("/checkout"); }}
                data-testid="product-buy-now-button"
                className="flex-1 rounded-full border border-[#4b3a2f] px-8 py-4 text-sm font-semibold text-[#4b3a2f] transition-colors duration-300 hover:bg-[#4b3a2f] hover:text-[#f2e9dd] sm:flex-none"
              >
                Comprar ahora
              </button>
            </div>

            <div className="mt-8 flex items-start gap-3 rounded-2xl bg-[#f2e9dd] p-5 text-sm text-[#6b5b52]">
              <ShieldCheck size={20} className="mt-0.5 shrink-0 text-[#3b7a57]" />
              <p>
                Compra 100% confidencial. Necesitas una cuenta AWEN para completar tu compra; el pago se procesa de forma segura con PayPal.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
